import type { SelectableCountry } from '@/types';

import React from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useRouter, useSearchParams } from 'next/navigation';
import { useShallow } from 'zustand/react/shallow';

import { GoToSignUpButton } from '@/app/auth/sign-in/_components';
import { Box, Button, Input, Link, MobileInput, Stack } from '@/components';
import { useInputAttributes, useLogin, useTranslation } from '@/hooks';
import useIsAdmin from '@/hooks/store/useIsAdmin';
import { routes } from '@/statics';
import {
  englishOnlyParams,
  mobileRegex,
  setCookie,
  setLocalStorage,
  yupValidator,
} from '@/utils';

type PasswordLoginFormData = {
  username: string;
  password: string;
};

const PasswordLoginForm: React.FC = () => {
  const router = useRouter();

  const searchParams = useSearchParams();
  const toggleUserType = useIsAdmin(
    useShallow((state) => state.toggleUserType)
  );

  const { t } = useTranslation('pages.signIn.form.passwordLogin');

  const [navigateLoading, setNavigateLoading] = React.useState<boolean>(false);
  const [selectedCountry, setSelectedCountry] =
    React.useState<SelectableCountry>('iran');

  const validationSchema = yupValidator.object().shape({
    username: yupValidator
      .string()
      .max(50)
      .test({ ...englishOnlyParams })
      .matches(mobileRegex[selectedCountry], t('invalidNumber'))
      .label(t('username'))
      .required(),
    password: yupValidator
      .string()
      .test({ ...englishOnlyParams })
      .label(t('password'))
      .required(),
  });

  const {
    handleSubmit,
    control,
    register,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
  });

  const filedInputProps = useInputAttributes<PasswordLoginFormData>(
    control,
    register,
    errors,
    {
      username: t('username'),
      password: t('password'),
    }
  );

  const { mutateAsync: login, isPending } = useLogin();

  const onSubmit = (data: PasswordLoginFormData) => {
    setNavigateLoading(true);

    void login(data)
      .then(async ({ token }) => {
        await setCookie({ name: 'lia-token', value: token });
        setLocalStorage({ name: 'lia-token', value: token });
        toggleUserType(false);
        router.replace(searchParams.get('returnURL') || routes.dashboard.url);
      })
      .catch(() => setNavigateLoading(false));
  };

  return (
    <Stack
      w="100%"
      h="100%"
      align="flex-start"
      gap={4}
      pt={4}
      display="flex"
      flexDir="column"
      flex={1}
      as="form"
    >
      <Box gap={4} w="full" display="flex" flexDirection="column">
        <MobileInput
          inputProps={{ ...filedInputProps.username }}
          selectedCountry={selectedCountry}
          setSelectedCountry={setSelectedCountry}
        />
        <Input isPassword {...filedInputProps.password} isLeftToRight />
        <Button
          href={routes.forgetPassword.url}
          as={Link}
          variant="link"
          mt={2}
          w="fit-content"
          lineHeight="16px"
        >
          {t('forgetPassword')}
        </Button>
      </Box>
      <Stack w="full" align="center" gap={6} justify="end" flexGrow={1}>
        <Button
          type="submit"
          w="full"
          onClick={(data) => void handleSubmit(onSubmit)(data)}
          isLoading={isPending || navigateLoading}
        >
          {t('enter')}
        </Button>
        <GoToSignUpButton />
      </Stack>
    </Stack>
  );
};
export default PasswordLoginForm;
